import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  FileSpreadsheet,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import type {
  DataTableCatalog,
  DataTablePreview,
  DataTableSummary,
  JobFieldDefinition,
} from '../shared/desktop-api';
import { fieldLookups, usesListSpelling } from '../shared/lookup-rules';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';
import type { PublishNotice } from './WorkspaceNotice';
import { OperatorErrorText } from './TechnicalDetails';
import './lookups-entry.css';

export type ListField = Pick<JobFieldDefinition, 'id' | 'name' | 'kind' | 'lookups'>;
export type ListDocumentType = { id: string; name: string; fields: readonly ListField[] };
type CommonProps = {
  jobId: string;
  onChanged?: () => void;
  onToast?: PublishNotice;
  /** Bumped by the parent after a lookup is saved elsewhere, so "Used by" stays true. */
  revision?: number;
  /** Where the list sheet is drawn: a layer over the setup content that does not scroll with it. */
  sheetHost?: HTMLElement | null;
  /** The lookup editor is open on top of the list sheet. */
  covered?: boolean;
};
type JobProps = CommonProps & {
  documentTypes: readonly ListDocumentType[];
  fields?: readonly ListField[];
  onOpenField?: (typeId: string, fieldId: string, dataTableId?: string) => void;
};
type LegacyProps = CommonProps & {
  documentTypes?: undefined;
  fields?: readonly ListField[];
  onOpenField?: (fieldId: string, dataTableId?: string) => void;
};
type Connection = { type: ListDocumentType; field: ListField; ruleId: string };
type SheetTab = 'rows' | 'uses' | 'file';
type FileAction = 'rename' | 'replace' | 'delete' | null;
const plural = (count: number, noun: string) =>
  `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;
const reasonWhy = (reason: unknown, fallback: string) =>
  reason instanceof Error ? reason.message : fallback;
const updatedOn = (table: DataTableSummary) =>
  table.uploadedAt && Number.isFinite(Date.parse(table.uploadedAt))
    ? new Date(table.uploadedAt).toLocaleDateString()
    : '';
const scalar = (field: ListField) => field.kind !== 'document-type' && field.kind !== 'line-items';
const PAGE_SIZE = 10;
const COLUMNS_SHOWN = 4;
/** A tile shows the list's own first rows; it draws as many as its height holds. */
const PEEK_ROWS = 20;
const PEEK_COLUMNS = 6;
const TABS: readonly [SheetTab, string][] = [
  ['rows', 'Rows'],
  ['uses', 'Used by'],
  ['file', 'File'],
];
/** A tile's rows are read once per version of the list's file: a replaced file has a new checksum. */
type Peek = { columnNames: readonly string[]; rows: readonly (readonly string[])[] } | 'failed';
const peekKey = (table: DataTableSummary) => `${table.id}:${table.checksum}:${table.rowCount}`;

/** Associations come only from saved rule IDs on fields. A matching name in
 * another document type is not evidence that the type uses this list. */
export function listConnections(
  types: readonly ListDocumentType[],
  catalog: DataTableCatalog,
  tableId: string,
): Connection[] {
  const rules = new Set(
    catalog.rules.filter((rule) => rule.dataTableId === tableId).map((rule) => rule.id),
  );
  return types.flatMap((type) =>
    type.fields.flatMap((field) =>
      [...new Set(fieldLookups(field).map((assignment) => assignment.ruleId))]
        .filter((id) => rules.has(id))
        .map((ruleId) => ({ type, field, ruleId })),
    ),
  );
}

function ListArtwork() {
  return (
    <svg
      className="lists-art"
      viewBox="0 0 160 94"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path className="lists-art-wire" d="M84 47h20m0 0V23h15m-15 24h15m-15 0v24h15" />
      <rect className="lists-art-paper" x="9" y="12" width="75" height="70" rx="7" />
      <path d="M9 33h75M9 49h75M9 65h75M37 33v49" />
      <path
        className="lists-art-accent"
        d="M19 23h17m11 0h24M18 42h10m17 0h27M18 58h10m17 0h27M18 73h10m17 0h27"
      />
      {[15, 39, 63].map((y) => (
        <g className="lists-art-output" key={y}>
          <rect x="119" y={y} width="31" height="16" rx="4" />
          <path d={`M126 ${y + 8}h16`} />
        </g>
      ))}
      <circle className="lists-art-pulse" cx="104" cy="47" r="3" />
    </svg>
  );
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * Lookup lists: every list the job's fields can look a value up in, as a library
 * of tiles. Opening a tile slides a sheet over the page with the list's rows, the
 * fields that use it, and its file. Editing a lookup opens the one lookup editor
 * Document types opens, on top of that sheet, so there is one way to connect a
 * field to a list from either page and no screen swap to find the way back from.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function LookupsPanel(props: JobProps | LegacyProps) {
  const { jobId, onChanged, onToast, revision = 0, sheetHost, covered = false } = props;
  const types = useMemo<readonly ListDocumentType[]>(
    () => props.documentTypes ?? [{ id: '', name: 'Document fields', fields: props.fields ?? [] }],
    [props.documentTypes, props.fields],
  );
  const fields = types.flatMap((type) => type.fields);
  const signature = JSON.stringify(
    fields.map((field) => [field.id, field.name, field.lookups ?? null]),
  );
  const [catalog, setCatalog] = useState<DataTableCatalog | null>(null);
  const [error, setError] = useState('');
  /** What telemetry sends for the error shown: the lists not read, a list not imported, or a change not saved. */
  const [errorCode, setErrorCode] = useState<TelemetryUiErrorCode>('lookup-load-failed');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [importName, setImportName] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [openId, setOpenId] = useState('');
  const [tab, setTab] = useState<SheetTab>('rows');
  const [fileAction, setFileAction] = useState<FileAction>(null);
  const [renameValue, setRenameValue] = useState('');
  const [choosingField, setChoosingField] = useState(false);
  const [fieldQuery, setFieldQuery] = useState('');
  const [preview, setPreview] = useState<DataTablePreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [columnOffset, setColumnOffset] = useState(0);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [rowQuery, setRowQuery] = useState('');
  const [rowSearch, setRowSearch] = useState('');
  const generation = useRef(0);
  const readRequest = useRef(0);
  const writing = useRef(false);
  const opener = useRef<HTMLElement | null>(null);
  const sheetRef = useRef<HTMLElement | null>(null);
  const libraryRef = useRef<HTMLDivElement | null>(null);
  const baseId = useId();
  // Each tile's first rows, read once it comes on screen and kept for this job.
  const [peeks, setPeeks] = useState<ReadonlyMap<string, Peek>>(() => new Map());
  const [onScreen, setOnScreen] = useState<ReadonlySet<string>>(() => new Set());
  const peeksAsked = useRef(new Set<string>());
  useEffect(() => {
    generation.current += 1;
    setCatalog(null);
    setOpenId('');
    setAdding(false);
    setNotice('');
    setWarnings([]);
    setQuery('');
    setFieldQuery('');
    setBusy(false);
    writing.current = false;
    setPeeks(new Map());
    setOnScreen(new Set());
    peeksAsked.current = new Set();
    return () => {
      generation.current += 1;
    };
  }, [jobId]);

  const refresh = useCallback(async () => {
    const request = ++readRequest.current;
    const owner = generation.current;
    const desktop = window.capturePointDesktop;
    if (!desktop) {
      setErrorCode('lookup-load-failed');
      setError('Lookup lists are unavailable. Reopen Job setup to try again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await desktop.listDataTables(jobId);
      if (owner !== generation.current || request !== readRequest.current) return;
      setCatalog(next);
      setError('');
      setOpenId((current) => (next.tables.some((table) => table.id === current) ? current : ''));
    } catch (reason) {
      if (owner === generation.current && request === readRequest.current) {
        setErrorCode('lookup-load-failed');
        setError(reasonWhy(reason, 'The lists for this job could not be read. Try again.'));
      }
    } finally {
      if (owner === generation.current && request === readRequest.current) setLoading(false);
    }
  }, [jobId, signature, revision]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    setOffset(0);
    setColumnOffset(0);
    setFileAction(null);
    setChoosingField(false);
    setFieldQuery('');
    setRowQuery('');
    setRowSearch('');
    setPreview(null);
  }, [openId]);
  // The search runs once typing pauses, and a new search starts from its first match.
  useEffect(() => {
    const wanted = rowQuery.trim();
    if (wanted === rowSearch) return;
    const timer = window.setTimeout(() => {
      setRowSearch(wanted);
      setOffset(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [rowQuery, rowSearch]);
  useEffect(() => {
    if (!openId || tab !== 'rows' || !window.capturePointDesktop) {
      setPreview(null);
      return;
    }
    let active = true;
    // The rows on screen stay until their replacement arrives, so a search that is
    // being typed narrows the table instead of blanking it on every pause.
    setPreviewError('');
    setPreviewLoading(true);
    window.capturePointDesktop
      .previewDataTable({
        jobId,
        dataTableId: openId,
        limit: PAGE_SIZE,
        offset,
        ...(rowSearch ? { query: rowSearch } : {}),
      })
      .then((result) => {
        if (active) setPreview(result);
      })
      .catch((reason) => {
        if (active) {
          setPreview(null);
          setPreviewError(reasonWhy(reason, 'These rows could not be read. Try again.'));
        }
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [jobId, openId, tab, offset, rowSearch, previewVersion]);
  // Keyboard focus follows the sheet in and back out to the tile that opened it,
  // and returns to the row that opened the lookup editor once that editor closes.
  const resumeFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (openId)
      window.requestAnimationFrame(() =>
        sheetRef.current?.querySelector<HTMLElement>('[data-sheet-start]')?.focus(),
      );
  }, [openId]);
  useEffect(() => {
    if (covered || !openId) return;
    const back = resumeFocus.current;
    resumeFocus.current = null;
    if (back)
      window.requestAnimationFrame(() =>
        (back.isConnected
          ? back
          : sheetRef.current?.querySelector<HTMLElement>('[data-sheet-start]')
        )?.focus(),
      );
  }, [covered, openId]);

  const tables = catalog?.tables ?? [];
  const open = tables.find((table) => table.id === openId);
  const connectionsFor = (tableId: string) =>
    catalog ? listConnections(types, catalog, tableId) : [];
  const connections = open ? connectionsFor(open.id) : [];
  const tableRules = catalog?.rules.filter((rule) => rule.dataTableId === openId) ?? [];
  const assignedRules = new Set(connections.map((connection) => connection.ruleId));
  const unattachedRules = tableRules.filter((rule) => !assignedRules.has(rule.id));
  const inUse = tableRules.length > 0 || !!open?.usedByRuleNames.length;
  // Saved lookups on this list that no field uses: they alone keep it from being
  // deleted, so deleting can take them with it once the operator says so.
  const onlyUnusedRules =
    connections.length === 0 &&
    tableRules.length > 0 &&
    unattachedRules.length === tableRules.length;
  const deleteBlocked = inUse && !onlyUnusedRules;
  const requiredColumns = [
    ...new Set(
      tableRules.flatMap((rule) =>
        [rule.findColumn, ...(rule.returns ?? []).map((entry) => entry.column)].filter(
          (column): column is string => !!column,
        ),
      ),
    ),
  ];
  const visible = tables.filter((table) =>
    `${table.name} ${table.columnNames.join(' ')}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );
  const allConnections = tables.flatMap((table) => connectionsFor(table.id));
  const usedTypeCount = new Set(allConnections.map((connection) => connection.type.id)).size;
  const canRename = typeof window.capturePointDesktop?.renameDataTable === 'function';
  // Tiles read their rows once they come on screen, so a job with many lists reads only what is shown.
  const visibleSignature = visible.map((table) => table.id).join('\n');
  useEffect(() => {
    const root = libraryRef.current;
    if (!root) return;
    const tiles = [...root.querySelectorAll<HTMLElement>('[data-list-id]')];
    const ids = (elements: Element[]) =>
      elements.map((element) => (element as HTMLElement).dataset.listId ?? '').filter(Boolean);
    const show = (shown: string[]) => {
      if (shown.length)
        setOnScreen((current) =>
          shown.every((id) => current.has(id)) ? current : new Set([...current, ...shown]),
        );
    };
    if (typeof IntersectionObserver !== 'function') {
      show(ids(tiles));
      return;
    }
    const watcher = new IntersectionObserver(
      (entries) =>
        show(ids(entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target))),
      { rootMargin: '240px 0px' },
    );
    tiles.forEach((tile) => watcher.observe(tile));
    return () => watcher.disconnect();
  }, [visibleSignature, Boolean(catalog)]);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    // While a list's sheet is open its own rows are being read; the tiles wait.
    if (typeof desktop?.previewDataTable !== 'function' || openId) return;
    const owner = generation.current;
    for (const table of catalog?.tables ?? []) {
      const key = peekKey(table);
      if (!onScreen.has(table.id) || peeksAsked.current.has(key)) continue;
      peeksAsked.current.add(key);
      desktop
        .previewDataTable({ jobId, dataTableId: table.id, limit: PEEK_ROWS, offset: 0 })
        .then(
          (result): Peek => ({ columnNames: result.columnNames, rows: result.rows }),
          (): Peek => 'failed',
        )
        .then((peek) => {
          if (owner === generation.current) setPeeks((current) => new Map(current).set(key, peek));
        });
    }
  }, [catalog, onScreen, openId, jobId]);
  const openField = (typeId: string, fieldId: string, dataTableId?: string) => {
    resumeFocus.current =
      document.activeElement instanceof HTMLElement &&
      sheetRef.current?.contains(document.activeElement)
        ? document.activeElement
        : null;
    if (props.documentTypes) (props as JobProps).onOpenField?.(typeId, fieldId, dataTableId);
    else (props as LegacyProps).onOpenField?.(fieldId, dataTableId);
  };
  const openSheet = (
    table: DataTableSummary,
    from: HTMLElement | null,
    nextTab: SheetTab = 'rows',
  ) => {
    opener.current = from;
    setOpenId(table.id);
    setTab(nextTab);
    setNotice('');
    setWarnings([]);
    setError('');
  };
  const closeSheet = () => {
    if (writing.current) return;
    setOpenId('');
    setNotice('');
    setWarnings([]);
    setError('');
    const back = opener.current;
    window.requestAnimationFrame(() => {
      if (back?.isConnected) back.focus();
    });
  };

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  /** One owner for every write: a second click, a job switch or a late reply cannot land twice. */
  const write = async (change: () => Promise<string | null>, failureCode: TelemetryUiErrorCode) => {
    if (writing.current || !window.capturePointDesktop) return;
    const owner = generation.current;
    writing.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setWarnings([]);
    try {
      const message = await change();
      if (owner !== generation.current || message === null) return;
      setNotice(message);
      onToast?.(message);
      onChanged?.();
      await refresh();
    } catch (reason) {
      if (owner === generation.current) {
        setErrorCode(failureCode);
        setError(reasonWhy(reason, 'That change could not be saved. Try again.'));
      }
    } finally {
      if (owner === generation.current) {
        writing.current = false;
        setBusy(false);
      }
    }
  };
  /** A list is named after its file unless the operator typed a name. */
  const submitAdd = () => {
    void addList();
  };
  const addList = () =>
    write(async () => {
      const name = importName.trim();
      const result = await window.capturePointDesktop!.importDataTable({ jobId, name });
      if (!result) return null;
      setAdding(false);
      setImportName('');
      setWarnings(result.warnings);
      opener.current = null;
      setOpenId(result.table.id);
      setTab('rows');
      setPreviewVersion((value) => value + 1);
      return `${result.table.name} added · ${plural(result.table.rowCount, 'row')}`;
    }, 'lookup-import-failed');
  const replaceList = (table: DataTableSummary) =>
    write(async () => {
      const result = await window.capturePointDesktop!.importDataTable({
        jobId,
        name: table.name,
        replacesDataTableId: table.id,
      });
      if (!result) return null;
      setWarnings(result.warnings);
      setFileAction(null);
      setTab('rows');
      setOffset(0);
      setColumnOffset(0);
      setPreviewVersion((value) => value + 1);
      return `${result.table.name} updated · ${plural(result.table.rowCount, 'row')}`;
    }, 'lookup-import-failed');
  const renameList = (table: DataTableSummary) =>
    write(async () => {
      const name = renameValue.trim();
      if (!name || name === table.name) {
        setFileAction(null);
        return null;
      }
      const renamed = await window.capturePointDesktop!.renameDataTable({
        jobId,
        dataTableId: table.id,
        name,
      });
      if (!renamed) throw new Error('The list was not renamed. Try again.');
      setFileAction(null);
      return `Renamed to ${name}`;
    }, 'lookup-save-failed');
  const deleteList = (table: DataTableSummary) =>
    write(async () => {
      if (deleteBlocked) return null;
      const desktop = window.capturePointDesktop!;
      // The engine refuses a lookup a saved field still uses, so a field assigned
      // since this panel was drawn stops the delete with its name.
      const unused = onlyUnusedRules ? unattachedRules : [];
      try {
        for (const rule of unused)
          if (!(await desktop.deleteLookupRule({ jobId, ruleId: rule.id })))
            throw new Error(`${rule.name} was not deleted. Refresh the lists and try again.`);
      } catch (reason) {
        // Some may be gone already: show what is left before saying what stopped.
        if (unused.length > 0) await refresh();
        throw reason;
      }
      const deleted = await desktop.deleteDataTable({ jobId, dataTableId: table.id });
      if (!deleted) throw new Error('The list was not deleted. Refresh the lists and try again.');
      setOpenId('');
      opener.current = null;
      return unused.length > 0
        ? `${table.name} and ${plural(unused.length, 'unused saved lookup')} deleted`
        : `${table.name} deleted`;
    }, 'lookup-save-failed');

  const notices = (
    <>
      {error && (
        <div
          className="lists-notice lists-error"
          role="alert"
          data-telemetry={`error:${errorCode}`}
        >
          <TriangleAlert size={17} />
          <OperatorErrorText message={error} />
          {!open && (
            <button
              className="secondary-button"
              type="button"
              disabled={busy || loading}
              onClick={() => void refresh()}
            >
              Refresh lists
            </button>
          )}
        </div>
      )}
      {notice && (
        <div className="lists-notice" role="status">
          <Check size={17} />
          <span>{notice}</span>
        </div>
      )}
      {warnings.map((warning, index) => (
        <div className="lists-notice lists-error" role="status" key={index}>
          <TriangleAlert size={17} />
          <span>{warning}</span>
        </div>
      ))}
    </>
  );

  const usesByType = (table: DataTableSummary) => {
    const linked = connectionsFor(table.id);
    return types
      .map((type) => ({
        type,
        count: linked.filter((connection) => connection.type.id === type.id).length,
      }))
      .filter((entry) => entry.count > 0);
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  /** The list itself, small: its real column headings and first rows. Until they are read (or if they cannot be),
   * the headings from the catalogue sit over sketched rows. */
  const peekOf = (table: DataTableSummary) => {
    const peek = peeks.get(peekKey(table));
    const read = peek && peek !== 'failed' ? peek : null;
    const columns = (read?.columnNames.length ? read.columnNames : table.columnNames).slice(
      0,
      PEEK_COLUMNS,
    );
    const row = (key: number, cells: ReactNode[], className = '') => (
      <span className={`lookup-list-peek-row${className}`} key={key}>
        {cells.map((cell, index) => (
          <span key={index}>{cell}</span>
        ))}
      </span>
    );
    return (
      <span
        className={`lookup-list-peek${read ? '' : peek === 'failed' ? ' is-unread' : ' is-waiting'}`}
        data-peek={read ? 'rows' : peek === 'failed' ? 'unread' : 'waiting'}
        aria-hidden="true"
      >
        {row(
          -1,
          columns.map((column) => column),
          ' is-head',
        )}
        {read
          ? read.rows.map((values, index) =>
              row(
                index,
                columns.map((_, column) => values[column] ?? ''),
              ),
            )
          : Array.from({ length: 8 }, (_, index) =>
              row(
                index,
                columns.map(() => <i />),
                ' is-sketch',
              ),
            )}
        {read && read.rows.length === 0 && <span className="lookup-list-peek-none">No rows</span>}
      </span>
    );
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const tile = (table: DataTableSummary) => {
    const uses = usesByType(table);
    const updated = updatedOn(table);
    return (
      <button
        type="button"
        className={`lookup-list-tile${table.id === openId ? ' is-open' : ''}`}
        key={table.id}
        data-list-id={table.id}
        aria-haspopup="dialog"
        disabled={busy}
        onClick={(event) => openSheet(table, event.currentTarget)}
      >
        <span className="lookup-list-title">
          <span className="lookup-list-icon" aria-hidden="true">
            <FileSpreadsheet size={17} strokeWidth={1.7} />
          </span>
          <strong>{table.name}</strong>
        </span>
        <small>
          {plural(table.rowCount, 'row')}
          <span aria-hidden="true"> · </span>
          {plural(table.columnNames.length, 'column')}
          {updated && (
            <>
              <span aria-hidden="true"> · </span>Updated {updated}
            </>
          )}
        </small>
        {peekOf(table)}
        <span className="lookup-list-uses">
          {uses.length ? (
            uses.map(({ type, count }) => (
              <span className="lookup-list-use" key={type.id}>
                {type.name} · {plural(count, 'field')}
              </span>
            ))
          ) : (
            <span className="lookup-list-unused">Not used yet</span>
          )}
        </span>
      </button>
    );
  };

  /** The searched text inside a cell, marked wherever it appears, ignoring capitals. */
  const marked = (text: string, query: string): ReactNode => {
    const wanted = query.toLocaleLowerCase();
    if (!wanted) return text;
    const lower = text.toLocaleLowerCase(),
      parts: ReactNode[] = [];
    let from = 0;
    for (let at = lower.indexOf(wanted); at >= 0; at = lower.indexOf(wanted, from)) {
      if (at > from) parts.push(text.slice(from, at));
      parts.push(<mark key={at}>{text.slice(at, at + wanted.length)}</mark>);
      from = at + wanted.length;
    }
    if (!parts.length) return text;
    if (from < text.length) parts.push(text.slice(from));
    return parts;
  };
  const rowsSummary = (page: DataTablePreview) => {
    const total = page.totalRowCount,
      range = `${(page.offset + 1).toLocaleString()}–${(page.offset + page.rows.length).toLocaleString()}`;
    if (!page.query)
      return total ? `Rows ${range} of ${total.toLocaleString()}` : 'No rows in this list';
    if (!total) return `No row has “${page.query}”`;
    return total <= PAGE_SIZE
      ? `${plural(total, 'row')} with “${page.query}”`
      : `Rows ${range} of ${total.toLocaleString()} with “${page.query}”`;
  };
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const rowsPanel = (table: DataTableSummary) => {
    const shown = preview?.columnNames.slice(columnOffset, columnOffset + COLUMNS_SHOWN) ?? [];
    const search = preview?.query ?? '';
    const matchesElsewhere =
      !!search &&
      !!preview?.rows.some(
        (row) =>
          !shown.some((_, col) =>
            (row[columnOffset + col] ?? '')
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()),
          ),
      );
    return (
      <>
        <label className="lists-search lookup-list-row-search">
          <Search size={16} />
          <input
            aria-label="Find a row"
            placeholder={`Find a row in ${plural(table.rowCount, 'row')}`}
            maxLength={200}
            value={rowQuery}
            onChange={(event) => setRowQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && rowQuery) {
                event.stopPropagation();
                setRowQuery('');
              }
            }}
          />
          {rowQuery && (
            <button
              type="button"
              className="icon-button"
              aria-label="Clear search"
              onClick={() => setRowQuery('')}
            >
              <X size={14} />
            </button>
          )}
        </label>
        <div className="lists-preview" aria-label="List preview" aria-busy={previewLoading}>
          {previewLoading && !preview && (
            <p className="lists-loading" role="status">
              <LoaderCircle size={17} className="spin" />
              Loading rows…
            </p>
          )}
          {previewError && (
            <div
              className="lists-notice lists-error"
              role="alert"
              data-telemetry="error:lookup-load-failed"
            >
              <span>{previewError}</span>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setPreviewVersion((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          )}
          {preview && (
            <>
              <div className="lists-preview-toolbar">
                <span role="status">{rowsSummary(preview)}</span>
                <div>
                  <button
                    className="icon-button"
                    type="button"
                    aria-label="Previous rows"
                    disabled={offset === 0 || previewLoading}
                    onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="icon-button"
                    type="button"
                    aria-label="Next rows"
                    disabled={
                      offset + preview.rows.length >= preview.totalRowCount || previewLoading
                    }
                    onClick={() => setOffset((value) => value + PAGE_SIZE)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              {preview.rows.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="lists-rownum">
                        Row
                      </th>
                      {shown.map((column, index) => (
                        <th key={index} scope="col">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, index) => (
                      <tr key={preview.rowNumbers?.[index] ?? preview.offset + index}>
                        <td className="lists-rownum">
                          {(
                            preview.rowNumbers?.[index] ?? preview.offset + index + 1
                          ).toLocaleString()}
                        </td>
                        {shown.map((column, col) => (
                          <td key={column} title={row[columnOffset + col] ?? ''}>
                            {row[columnOffset + col] ? (
                              marked(row[columnOffset + col], search)
                            ) : (
                              <span className="lists-blank">Empty</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {matchesElsewhere && (
                <p className="lookup-list-hint lookup-list-elsewhere">
                  Some rows match in columns not shown here. Use Columns to see them.
                </p>
              )}
              {preview.columnNames.length > COLUMNS_SHOWN && (
                <div className="lists-column-pager">
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={columnOffset === 0}
                    onClick={() => setColumnOffset((value) => Math.max(0, value - COLUMNS_SHOWN))}
                  >
                    <ChevronLeft size={14} />
                    Columns
                  </button>
                  <span>
                    {columnOffset + 1}–
                    {Math.min(columnOffset + COLUMNS_SHOWN, preview.columnNames.length)} of{' '}
                    {preview.columnNames.length}
                  </span>
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={columnOffset + COLUMNS_SHOWN >= preview.columnNames.length}
                    onClick={() => setColumnOffset((value) => value + COLUMNS_SHOWN)}
                  >
                    Columns
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <p className="lookup-list-hint">
          Rows come from {table.sourceFileName}.{' '}
          <button
            type="button"
            className="lookup-list-inline-action"
            onClick={() => {
              setTab('file');
              setFileAction('replace');
            }}
          >
            Replace the file
          </button>{' '}
          when it changes.
        </p>
      </>
    );
  };

  const connectionRows = (table: DataTableSummary, interactive: boolean) =>
    types
      .filter((type) => connections.some((connection) => connection.type.id === type.id))
      .map((type) => (
        <section className="lookup-list-use-group" key={type.id}>
          <h4>{type.name}</h4>
          <ul>
            {connections
              .filter((connection) => connection.type.id === type.id)
              .map(({ field, ruleId }) => {
                const assignment = fieldLookups(field).find(
                  (candidate) => candidate.ruleId === ruleId,
                );
                const mustBeOn = usesListSpelling(
                  field,
                  assignment,
                  catalog?.rules.find((rule) => rule.id === ruleId),
                );
                const source =
                  assignment?.sourceFieldId && assignment.sourceFieldId !== field.id
                    ? type.fields.find((candidate) => candidate.id === assignment.sourceFieldId)
                    : undefined;
                const detail = mustBeOn
                  ? "Uses this list's spelling"
                  : source
                    ? `Filled from this list · looked up by ${source.name}`
                    : 'Filled from this list';
                const content = (
                  <>
                    <span
                      className={`lookup-list-role${mustBeOn ? ' must' : ''}`}
                      aria-hidden="true"
                    >
                      {mustBeOn ? <ShieldCheck size={14} /> : <CornerDownRight size={14} />}
                    </span>
                    <span>
                      <strong>{field.name}</strong>
                      <small>{detail}</small>
                    </span>
                  </>
                );
                return (
                  <li key={`${field.id}:${ruleId}`}>
                    {interactive ? (
                      <button
                        type="button"
                        className="lookup-list-use-row"
                        aria-label={`${field.name}, ${type.name}: ${detail}`}
                        disabled={busy || !props.onOpenField}
                        onClick={() => openField(type.id, field.id, table.id)}
                      >
                        {content}
                        <ChevronRight size={16} />
                      </button>
                    ) : (
                      <div className="lookup-list-use-row">{content}</div>
                    )}
                  </li>
                );
              })}
          </ul>
        </section>
      ));

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const fieldChooser = (table: DataTableSummary) => {
    const already = new Set(
      connections.map((connection) => `${connection.type.id}:${connection.field.id}`),
    );
    const total = types.reduce((count, type) => count + type.fields.filter(scalar).length, 0);
    const wanted = fieldQuery.trim().toLocaleLowerCase();
    const groups = types
      .map((type) => ({
        type,
        fields: type.fields.filter(
          (field) =>
            scalar(field) &&
            !already.has(`${type.id}:${field.id}`) &&
            `${type.name} ${field.name}`.toLocaleLowerCase().includes(wanted),
        ),
      }))
      .filter((group) => group.fields.length > 0);
    return (
      <div className="lookup-list-field-chooser">
        <div className="lookup-list-chooser-head">
          <h4>Which field should look up its value here?</h4>
          <button
            type="button"
            className="icon-button"
            aria-label="Cancel choosing a field"
            onClick={() => setChoosingField(false)}
          >
            <X size={16} />
          </button>
        </div>
        {total > 10 && (
          <label className="lists-search">
            <Search size={16} />
            <input
              aria-label="Find a field"
              placeholder="Find a field or document type"
              value={fieldQuery}
              autoFocus
              onChange={(event) => setFieldQuery(event.target.value)}
            />
          </label>
        )}
        {groups.map(({ type, fields: choices }) => (
          <section className="lists-field-group" key={type.id}>
            {types.length > 1 && <h5>{type.name}</h5>}
            <div className="lookups-field-choices">
              {choices.map((field) => (
                <button
                  type="button"
                  className="lookups-field-choice"
                  key={field.id}
                  aria-label={`Look up ${field.name} in ${table.name}`}
                  disabled={busy || !props.onOpenField}
                  onClick={() => openField(type.id, field.id, table.id)}
                >
                  <span>
                    <strong>{field.name}</strong>
                    <small>
                      {fieldLookups(field).length > 0 ? 'Uses another list' : 'Read from the page'}
                    </small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              ))}
            </div>
          </section>
        ))}
        {groups.length === 0 && (
          <p className="lookup-list-hint">
            {total === 0 ? (
              <>
                Add a field in <strong>Document types</strong> first.
              </>
            ) : (
              'Every field already uses this list, or none matches.'
            )}
          </p>
        )}
      </div>
    );
  };

  const usesPanel = (table: DataTableSummary) => (
    <>
      {connections.length === 0 && unattachedRules.length === 0 && (
        <p className="lookup-list-hint">No field uses this list yet.</p>
      )}
      {connectionRows(table, true)}
      {unattachedRules.length > 0 && (
        <p className="lists-unassigned">
          Other saved lookups: {unattachedRules.map((rule) => rule.name).join(', ')}. No field in
          this job uses them.
        </p>
      )}
      {choosingField ? (
        fieldChooser(table)
      ) : (
        <div>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || !props.onOpenField}
            onClick={() => setChoosingField(true)}
          >
            <Plus size={16} />
            Use this list for a field
          </button>
        </div>
      )}
    </>
  );

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const filePanel = (table: DataTableSummary) => {
    const updated = updatedOn(table);
    const toggle = (action: FileAction) => {
      setFileAction((current) => (current === action ? null : action));
      if (action === 'rename') setRenameValue(table.name);
    };
    let panel: ReactNode = null;
    if (fileAction === 'rename')
      panel = (
        <form
          className="lists-change-panel"
          aria-label="Rename list"
          onSubmit={(event) => {
            event.preventDefault();
            void renameList(table);
          }}
        >
          <label>
            List name
            <input
              autoFocus
              maxLength={160}
              value={renameValue}
              disabled={busy}
              onChange={(event) => setRenameValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setFileAction(null);
                }
              }}
            />
          </label>
          <div className="lists-change-actions">
            <button
              className="secondary-button"
              type="button"
              disabled={busy}
              onClick={() => setFileAction(null)}
            >
              Cancel
            </button>
            <button className="primary-button" disabled={busy || !renameValue.trim()}>
              {busy ? <LoaderCircle size={16} className="spin" /> : <Check size={16} />}Save name
            </button>
          </div>
        </form>
      );
    if (fileAction === 'replace')
      panel = (
        <section className="lists-change-panel" aria-label="Review list update">
          <div>
            <h4>Replace {table.name}</h4>
            <p>The new file replaces every row. Lookups keep working with the new rows.</p>
          </div>
          {connections.length > 0 || unattachedRules.length > 0 ? (
            <>
              {connectionRows(table, false)}
              {unattachedRules.length > 0 && (
                <p className="lists-unassigned">
                  Other saved lookups: {unattachedRules.map((rule) => rule.name).join(', ')}.
                </p>
              )}
            </>
          ) : (
            <p>No field uses this list.</p>
          )}
          {requiredColumns.length > 0 && (
            <p>
              Keep these column headings: <strong>{requiredColumns.join(', ')}</strong>. A file
              missing them will be refused.
            </p>
          )}
          <div className="lists-change-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={busy}
              onClick={() => setFileAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              disabled={busy}
              onClick={() => void replaceList(table)}
            >
              {busy ? <LoaderCircle size={16} className="spin" /> : <Upload size={16} />}Choose
              replacement file
            </button>
          </div>
        </section>
      );
    if (fileAction === 'delete')
      panel = (
        <section
          className="lists-change-panel lookup-list-delete"
          aria-label="Review list deletion"
        >
          <div>
            <h4>Delete {table.name}?</h4>
            <p>
              {onlyUnusedRules
                ? `No field uses this list, but ${tableRules.length === 1 ? 'this saved lookup still points at it' : 'these saved lookups still point at it'}. Deleting the list deletes ${tableRules.length === 1 ? 'it' : 'them'} too. Your original file stays unchanged.`
                : inUse
                  ? 'Fields still look up values in this list. Open each one and switch it to read from the page, then delete the list.'
                  : 'This removes the list from this job. Your original file stays unchanged.'}
            </p>
          </div>
          {onlyUnusedRules ? (
            <p>
              Unused saved {tableRules.length === 1 ? 'lookup' : 'lookups'}:{' '}
              <strong>{unattachedRules.map((rule) => rule.name).join(', ')}</strong>
            </p>
          ) : (
            inUse &&
            (connections.length > 0
              ? connectionRows(table, true)
              : table.usedByRuleNames.length > 0 && (
                  <p>Saved lookups: {table.usedByRuleNames.join(', ')}</p>
                ))
          )}
          <div className="lists-change-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={busy}
              onClick={() => setFileAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="secondary-button lists-delete"
              disabled={busy || deleteBlocked}
              onClick={() => void deleteList(table)}
            >
              {busy ? <LoaderCircle size={16} className="spin" /> : <Trash2 size={16} />}
              {onlyUnusedRules
                ? `Delete the list and its ${plural(tableRules.length, 'unused saved lookup')}`
                : 'Delete list'}
            </button>
          </div>
        </section>
      );
    return (
      <>
        <div className="lookup-list-file">
          <FileSpreadsheet size={30} strokeWidth={1.5} />
          <div>
            <strong>{table.sourceFileName}</strong>
            <small>
              {[
                updated && `Updated ${updated}`,
                plural(table.rowCount, 'row'),
                plural(table.columnNames.length, 'column'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </small>
          </div>
        </div>
        <div className="lookup-list-columns">
          <h4>Columns</h4>
          <div>
            {table.columnNames.map((column) => (
              <span className="lookup-list-column" key={column}>
                {column}
                {requiredColumns.includes(column) && <small>in use</small>}
              </span>
            ))}
          </div>
        </div>
        <div className="lookup-list-file-actions">
          {canRename && (
            <button
              type="button"
              className="secondary-button"
              aria-expanded={fileAction === 'rename'}
              disabled={busy}
              onClick={() => toggle('rename')}
            >
              <Pencil size={15} />
              Rename
            </button>
          )}
          <button
            type="button"
            className="secondary-button"
            aria-expanded={fileAction === 'replace'}
            disabled={busy}
            onClick={() => toggle('replace')}
          >
            <RefreshCw size={15} />
            Replace file
          </button>
          <button
            type="button"
            className="secondary-button lists-delete"
            aria-expanded={fileAction === 'delete'}
            disabled={busy}
            onClick={() => toggle('delete')}
          >
            <Trash2 size={15} />
            Delete list
          </button>
        </div>
        {panel}
      </>
    );
  };

  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = TABS.findIndex(([id]) => id === tab);
    const next = TABS[(index + step + TABS.length) % TABS.length][0];
    setTab(next);
    window.requestAnimationFrame(() =>
      sheetRef.current
        ?.querySelector<HTMLElement>(`#${CSS.escape(`${baseId}-tab-${next}`)}`)
        ?.focus(),
    );
  };

  const sheet = open && (
    <>
      <div className="setup-sheet-scrim" aria-hidden="true" onClick={closeSheet} />
      <section
        ref={sheetRef}
        className={`setup-sheet lookup-list-sheet lists-workspace${covered ? ' is-under' : ''}`}
        role="dialog"
        aria-labelledby={`${baseId}-title`}
        inert={covered}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.stopPropagation();
            closeSheet();
          }
        }}
      >
        <header className="lookup-list-sheet-head">
          <span className="lookup-list-badge" aria-hidden="true">
            <FileSpreadsheet size={22} strokeWidth={1.6} />
          </span>
          <div>
            <p className="lookup-list-eyebrow">List · {open.sourceFileName}</p>
            <h3 id={`${baseId}-title`}>{open.name}</h3>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Close list"
            data-sheet-start
            disabled={busy}
            onClick={closeSheet}
          >
            <X size={18} />
          </button>
        </header>
        <div className="lookup-list-tabs" role="tablist" aria-label={`${open.name} details`}>
          {TABS.map(([id, label]) => (
            <button
              type="button"
              role="tab"
              key={id}
              id={`${baseId}-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`${baseId}-panel`}
              tabIndex={tab === id ? 0 : -1}
              onKeyDown={onTabKey}
              onClick={() => setTab(id)}
            >
              {label}
              {id === 'uses' && <span className="lookup-list-count">{connections.length}</span>}
            </button>
          ))}
        </div>
        <div
          className="lookup-list-sheet-body"
          role="tabpanel"
          id={`${baseId}-panel`}
          aria-labelledby={`${baseId}-tab-${tab}`}
        >
          {notices}
          {tab === 'rows' && rowsPanel(open)}
          {tab === 'uses' && usesPanel(open)}
          {tab === 'file' && filePanel(open)}
        </div>
      </section>
    </>
  );

  const summary =
    !catalog || tables.length === 0
      ? ''
      : `${plural(tables.length, 'list')} · ${allConnections.length ? `used by ${plural(allConnections.length, 'field')} on ${plural(usedTypeCount, 'document type')}` : 'not used by any field yet'}`;
  const first = Boolean(catalog) && tables.length === 0;
  const nameBox = (autoFocus: boolean) => (
    <input
      aria-label="List name"
      autoFocus={autoFocus}
      maxLength={160}
      value={importName}
      disabled={busy}
      onChange={(event) => setImportName(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !first) setAdding(false);
      }}
      placeholder="Named after the file"
    />
  );
  const chooseFile = (
    <button className="primary-button" disabled={busy}>
      {busy ? <LoaderCircle size={16} className="spin" /> : <Upload size={16} />}Choose file
    </button>
  );
  const openAdding = () => {
    setAdding(true);
    setImportName('');
    setNotice('');
    setWarnings([]);
  };
  return (
    <section
      className="lookups-card lists-workspace"
      aria-label="Lookup lists in this job"
      aria-busy={loading || busy}
    >
      <div
        className={`lookup-lists-library${first ? ' is-first' : ''}`}
        ref={libraryRef}
        inert={Boolean(open)}
      >
        <header className="lists-heading">
          <div>
            <h3>Lookup lists</h3>
            {summary && <p>{summary}</p>}
          </div>
          {tables.length > 0 && (
            <div className="lists-heading-tools">
              {tables.length > 4 && (
                <label className="lists-search">
                  <Search size={16} />
                  <input
                    aria-label="Find a list"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Find a list or column"
                  />
                </label>
              )}
              <button
                type="button"
                className="secondary-button"
                aria-expanded={adding}
                disabled={busy || loading || adding}
                onClick={openAdding}
              >
                <Plus size={16} />
                Add a list
              </button>
            </div>
          )}
        </header>
        {!open && notices}
        {loading && !catalog && (
          <p className="lists-loading" role="status">
            <LoaderCircle size={18} className="spin" />
            Loading lists…
          </p>
        )}
        {/* No lists yet: one large place to add the first, with its one action. */}
        {first && (
          <form
            className="lists-first"
            aria-labelledby={`${baseId}-first`}
            onSubmit={(event) => {
              event.preventDefault();
              submitAdd();
            }}
          >
            <ListArtwork />
            <h4 id={`${baseId}-first`}>Add your first list</h4>
            <p>Excel, CSV or text with headings</p>
            <div className="lists-first-row">{chooseFile}</div>
          </form>
        )}
        {catalog && tables.length > 0 && (
          <div className="lookup-lists-grid">
            {adding && (
              <form
                className="lookup-list-tile lookup-list-add-form"
                aria-label="Add a list"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitAdd();
                }}
              >
                <label>List name{nameBox(true)}</label>
                <small>Optional · named after the file otherwise</small>
                <div className="lists-change-actions">
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={busy}
                    onClick={() => setAdding(false)}
                  >
                    Cancel
                  </button>
                  {chooseFile}
                </div>
              </form>
            )}
            {visible.map(tile)}
          </div>
        )}
        {query && visible.length === 0 && (
          <p className="lookup-list-hint">No list or column matches “{query}”.</p>
        )}
      </div>
      {sheet &&
        (sheetHost ? (
          createPortal(sheet, sheetHost)
        ) : (
          <div className="lookup-list-inline-layer">{sheet}</div>
        ))}
    </section>
  );
}
